import React, { useState } from 'react';

import CardDialog from '~/components/CardDialog';
import { DIALOG_FOOTER_CLASS_NAME } from '~/components/Clive/CliveDialog/constants';
import Button from '~/components/core/Atomic/Buttons/Button';
import TextField from '~/components/core/Molecules/Fields/TextField';
import Text from '~/components/core/TextComponents/Text';

import { TEXTS } from './texts';

/** The longest reason the handling routes accept. */
const REASON_MAX_LENGTH = 2000;

interface TakeOverDialogProps {
  /** Resolves once the claim is taken over; closing the dialog is the caller's to do. */
  onConfirm: (reason: string | undefined) => Promise<void>;
  onClose: () => void;
}

/**
 * Asks before taking the claim over from Clive, with the handler's reason for it if they give one.
 * A blank reason is sent as none.
 */
const TakeOverDialog: React.FC<TakeOverDialogProps> = ({ onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onConfirm(reason.trim() || undefined);
      // No reset on success: `onConfirm` closes the dialog, so its buttons stay disabled until it goes.
    } catch {
      // `useSwitchHandling` already reported the failure. The dialog stays open with the reason the
      // handler wrote, to retry with.
      setIsSubmitting(false);
    }
  };

  const footerActions = (
    <div className="flex w-full justify-end gap-12">
      <Button variant="text" className="text-blue-600" onClick={onClose} disabled={isSubmitting}>
        {TEXTS.CANCEL}
      </Button>
      <Button variant="contained" color="primary" onClick={handleConfirm} disabled={isSubmitting}>
        {TEXTS.CONFIRM}
      </Button>
    </div>
  );

  return (
    <CardDialog
      isDialog
      title={TEXTS.DIALOG_TITLE}
      onClose={onClose}
      preventClose={isSubmitting}
      maxWidth="sm"
      fullWidth
      footerClassName={DIALOG_FOOTER_CLASS_NAME}
      footerActions={footerActions}
    >
      <div className="flex flex-col gap-16 pb-8 pt-4">
        <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {TEXTS.DIALOG_BODY}
        </Text>
        <TextField
          id="take-over-reason"
          label={TEXTS.REASON_LABEL}
          size="medium"
          value={reason}
          onChange={setReason}
          multiline
          minRows={3}
          fullWidth
          autoFocus
          disabled={isSubmitting}
          inputProps={{ maxLength: REASON_MAX_LENGTH }}
        />
      </div>
    </CardDialog>
  );
};

export default TakeOverDialog;
